import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { MapPin, Search, Locate, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icon
const createMarkerIcon = (color) => {
  return L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32" height="32"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`,
    className: "custom-marker",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Map center updater
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

export default function LocationPicker({
  label = "Select Location",
  value = null,
  onChange,
  placeholder = "Search for a location...",
  markerColor = "#1B4332",
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value);
  const [mapCenter, setMapCenter] = useState([34.0837, 74.7973]); // Default: Srinagar
  const searchTimeoutRef = useRef(null);

  // Initialize from value prop
  useEffect(() => {
    if (value && value.lat && value.lng) {
      setSelectedLocation(value);
      setMapCenter([value.lat, value.lng]);
    }
  }, [value]);

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await safeFetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { "Accept-Language": "en" } }
      );
      if (response.ok) {
        const data = await response.json();
        return data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    } catch (e) {
      console.error("Reverse geocode error:", e);
    }
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  // Search for locations
  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Search with focus on Kashmir region
      const response = await safeFetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&viewbox=73.5,35.5,77.5,32.5&bounded=0&limit=5`,
        { headers: { "Accept-Language": "en" } }
      );
      if (response.ok) {
        const results = await response.json();
        setSearchResults(
          results.map((r) => ({
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
            address: r.display_name,
            name: r.name || r.display_name.split(",")[0],
          }))
        );
      }
    } catch (e) {
      console.error("Search error:", e);
      toast.error("Search failed. Please try again.");
    }
    setSearching(false);
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(query);
    }, 500);
  };

  // Handle location selection from map click
  const handleMapClick = async (lat, lng) => {
    const address = await reverseGeocode(lat, lng);
    const location = { lat, lng, address };
    setSelectedLocation(location);
    setMapCenter([lat, lng]);
  };

  // Handle location selection from search results
  const handleSearchResultSelect = (result) => {
    setSelectedLocation(result);
    setMapCenter([result.lat, result.lng]);
    setSearchQuery(result.name);
    setSearchResults([]);
  };

  // Get current location
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.info("Getting your location...");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const address = await reverseGeocode(latitude, longitude);
        const location = { lat: latitude, lng: longitude, address };
        setSelectedLocation(location);
        setMapCenter([latitude, longitude]);
        toast.success("Location found!");
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          toast.error("Location permission denied");
        } else {
          toast.error("Could not get your location");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Confirm selection
  const handleConfirm = () => {
    if (selectedLocation) {
      onChange(selectedLocation);
      setIsOpen(false);
    } else {
      toast.error("Please select a location on the map");
    }
  };

  const markerIcon = createMarkerIcon(markerColor);

  return (
    <div className={className}>
      {/* Trigger Button */}
      <div
        data-testid="location-picker-trigger"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-forest transition-colors bg-white"
      >
        <MapPin className={`w-5 h-5 ${value ? "text-forest" : "text-slate-400"}`} />
        <span className={`flex-1 text-sm ${value ? "text-slate-900" : "text-slate-400"}`}>
          {value?.address ? value.address.substring(0, 50) + (value.address.length > 50 ? "..." : "") : placeholder}
        </span>
        {value && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setSelectedLocation(null);
            }}
            className="p-1 hover:bg-slate-100 rounded-full"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-lg text-slate-900">{label}</h3>
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="location-search-input"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search for a place..."
                    className="pl-10 rounded-xl"
                  />
                  {searching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-forest border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <Button
                  data-testid="get-current-location-btn"
                  variant="outline"
                  onClick={handleGetCurrentLocation}
                  className="rounded-xl"
                >
                  <Locate className="w-4 h-4" />
                </Button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                  {searchResults.map((result, idx) => (
                    <div
                      key={idx}
                      data-testid={`search-result-${idx}`}
                      onClick={() => handleSearchResultSelect(result)}
                      className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <p className="font-medium text-sm text-slate-900">{result.name}</p>
                      <p className="text-xs text-slate-500 truncate">{result.address}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Map */}
            <div className="h-[300px] relative">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapUpdater center={mapCenter} />
                <MapClickHandler onLocationSelect={handleMapClick} />

                {selectedLocation && (
                  <Marker
                    position={[selectedLocation.lat, selectedLocation.lng]}
                    icon={markerIcon}
                  />
                )}
              </MapContainer>

              {/* Click hint */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full">
                Click on map to select location
              </div>
            </div>

            {/* Selected Location Display */}
            {selectedLocation && (
              <div className="p-4 bg-forest-50 border-t border-forest/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-forest flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">Selected Location</p>
                    <p className="text-xs text-slate-600 mt-0.5 break-words">{selectedLocation.address}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-location-btn"
                onClick={handleConfirm}
                disabled={!selectedLocation}
                className="flex-1 bg-forest hover:bg-forest-dark text-white rounded-xl"
              >
                <Check className="w-4 h-4 mr-2" /> Confirm Location
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
